/**
 * React hook for client-side E2E encryption of entity fields.
 * Uses the encryption key stored in localStorage for the given clientId.
 */
import { useCallback, useMemo } from "react";
import {
  getClientKey,
  encrypt as rawEncrypt,
  decrypt as rawDecrypt,
  encryptBinary as rawEncryptBinary,
  decryptBinary as rawDecryptBinary,
  encryptFields as rawEncryptFields,
  decryptFields as rawDecryptFields,
} from "@/lib/crypto";

export function useEncryption(clientId: string | undefined) {
  const key = useMemo(
    () => (clientId ? getClientKey(clientId) : null),
    [clientId],
  );

  const hasKey = !!key;

  /** Encrypt a single string value. Returns original if no key. */
  const encryptValue = useCallback(
    async (value: string): Promise<string> => {
      if (!key || !value) return value;
      return rawEncrypt(value, key);
    },
    [key],
  );

  /** Decrypt a single string value. Returns "[Encrypted]" on failure. */
  const decryptValue = useCallback(
    async (value: string): Promise<string> => {
      if (!key || !value) return value;
      return rawDecrypt(value, key);
    },
    [key],
  );

  /** Encrypt specified fields of an object. Returns unchanged if no key. */
  const encryptFields = useCallback(
    async <T extends Record<string, unknown>>(
      obj: T,
      fields: (keyof T)[],
    ): Promise<T> => {
      if (!key) return obj;
      return rawEncryptFields(obj, fields, key);
    },
    [key],
  );

  /** Decrypt specified fields of an object. Returns unchanged if no key. */
  const decryptFields = useCallback(
    async <T extends Record<string, unknown>>(
      obj: T,
      fields: (keyof T)[],
    ): Promise<T> => {
      if (!key) return obj;
      return rawDecryptFields(obj, fields, key);
    },
    [key],
  );

  /** Decrypt a list of objects. */
  const decryptList = useCallback(
    async <T extends Record<string, unknown>>(
      items: T[],
      fields: (keyof T)[],
    ): Promise<T[]> => {
      if (!key) return items;
      return Promise.all(items.map((item) => rawDecryptFields(item, fields, key)));
    },
    [key],
  );

  /** Encrypt raw binary data. Returns base64 string or null if no key. */
  const encryptBinary = useCallback(
    async (data: Uint8Array): Promise<string | null> => {
      if (!key) return null;
      return rawEncryptBinary(data, key);
    },
    [key],
  );

  /** Decrypt binary data encrypted with encryptBinary. Returns Uint8Array or null. */
  const decryptBinary = useCallback(
    async (encrypted: string): Promise<Uint8Array | null> => {
      if (!key) return null;
      return rawDecryptBinary(encrypted, key);
    },
    [key],
  );

  return { hasKey, encryptValue, decryptValue, encryptFields, decryptFields, decryptList, encryptBinary, decryptBinary };
}
